import localforage from 'localforage'

import type {DefaultRootState} from '../coreReducer'
import type {ILocalStorage, StateKeys} from '../store'

export interface IAuthStorage {
	name: string
	storeName: string
	items: StateKeys[]
}

const isDateTime = (value: any): boolean => {
	return value && typeof value === 'object' && value.isLuxonDateTime === true
}

export const serializeDateTimes = (obj: any): any => {
	if (!obj || typeof obj !== 'object') {
		return obj
	}

	if (Array.isArray(obj)) {
		return obj.map(item => serializeDateTimes(item))
	}

	if (isDateTime(obj)) {
		return obj.toISO()
	}

	const result: any = {}
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			result[key] = serializeDateTimes(obj[key])
		}
	}

	return result
}

class LocalStorage implements ILocalStorage {
	private readonly storage: LocalForage

	private readonly items: StateKeys[]

	constructor(obj: IAuthStorage) {
		this.items = obj.items
		this.storage = localforage.createInstance({
			driver: localforage.INDEXEDDB,
			name: obj.name,
			storeName: obj.storeName,
		})
	}

	persist(state: Partial<DefaultRootState>): Promise<void>[] {
		const promises: Promise<void>[] = []
		this.items.forEach(item => {
			if (state[item] !== undefined) {
				promises.push(this.setItem(item as string, state[item]).then(() => Promise.resolve()))
			}
		})
		return promises
	}

	async retrieve(): Promise<Partial<DefaultRootState>> {
		const promises: Promise<Partial<DefaultRootState>>[] = this.items.map(obj => {
			return new Promise(
				resolve =>
					void this.getItem<Partial<DefaultRootState>>(obj as string).then(data => {
						if (data !== null) {
							resolve({ [obj]: data })
						} else {
							resolve({})
						}
					}),
			)
		})

		return Promise.all(promises).then(results => {
			return results.reduce((acc: Partial<DefaultRootState>, val) => {
				return {
					...acc,
					...val,
				}
			}, {} as Partial<DefaultRootState>)
		})
	}

	clear(): Promise<void> {
		throw new Error('Method not implemented.')
	}

	async getItem<T>(key: string): Promise<T | null> {
		return this.storage.getItem<T>(key).then(res => {
			if (res === null) {
				return Promise.resolve(null)
			}
			return Promise.resolve(res)
		})
	}

	removeItem(key: string): Promise<void> {
		return this.storage.removeItem(key)
	}

	setItem<T>(key: string, value: T): Promise<T> {
		return this.storage.setItem<T>(key, serializeDateTimes(value))
	}

	getItems(): string[] {
		return this.items
	}
}

export { LocalStorage }
